<template>
  <div class="space-y-4">
    <div class="mt-4 grid gap-4 md:grid-cols-3">
      <LabeledText :label="t('renter')"
        >{{ bookingAllocation.booking?.customerData?.firstName ?? 'n/a' }}
        {{ bookingAllocation.booking?.customerData?.lastName ?? 'n/a' }}</LabeledText
      >
      <LabeledText :label="t('bookingNumber')">
        <span v-if="bookingAllocation.booking" class="inline-flex items-center gap-1">
          <RouterLink
            :to="{ name: 'bookingPlanEdit', params: { id: bookingAllocation.booking.id } }"
          >
            {{ bookingAllocation.booking.bookingNumber }}
          </RouterLink>
          <CopyToClipboardButton :text="bookingAllocation.booking.bookingNumber" />
        </span>
        <template v-else>n/a</template>
      </LabeledText>
      <TextareaField name="comment" :label="t('comment')" empty-value-to-null />
      <SearchUserInput
        :label="t('driver')"
        clearable
        :allowed-roles="driverRoles"
        :selected-user="selectedDriver"
        :placeholder="t('noDriverSelected')"
        @update-user="handleDriverUpdate"
      />
    </div>
    <div v-if="meta.dirty" class="flex justify-end gap-4">
      <CVButton variant="default" :is-loading="isSaving" @click.prevent="handleSave">{{
        t('save')
      }}</CVButton>
      <CVButton variant="secondary" @click.prevent="handleCancel">{{ t('cancel') }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import TextareaField from '@/components/TextareaField.vue';
import SearchUserInput from '@/components/booking-forms/customer/SearchUserInput.vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { UserRole } from '@/entities/auth/user-role.enum';
import type { User } from '@/entities/auth/user.entity';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import { useForm } from 'vee-validate';
import CVButton from '@/components/buttons/CVButton.vue';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { ref, watch } from 'vue';

const props = defineProps<{
  transfer: Allocation;
  bookingAllocation: Allocation;
}>();

const { t } = useI18n();

const driverRoles = [UserRole.ADMIN, UserRole.HUB_MANAGER, UserRole.HUB_AGENT, UserRole.OPERATOR];

const selectedDriver = ref<User | undefined>(props.transfer.driver ?? undefined);

const { handleSubmit, meta, resetForm, setFieldValue } = useForm({
  initialValues: {
    driverId: props.transfer.driver?.id ?? null,
    comment: props.transfer.comment ?? null,
  },
  keepValuesOnUnmount: true,
});

const handleDriverUpdate = (user: User | undefined) => {
  selectedDriver.value = user;
  setFieldValue('driverId', user?.id ?? null);
};

const handleCancel = () => {
  resetForm();
  selectedDriver.value = props.transfer.driver ?? undefined;
};

watch(
  () => props.transfer.driver,
  (driver) => {
    if (!meta.value.dirty) {
      selectedDriver.value = driver ?? undefined;
    }
  },
);

const { mutateAsync: updateAllocation, isPending: isSaving } = useUpdateAllocation();

const handleSave = handleSubmit(async (values) => {
  await updateAllocation({
    id: props.transfer.id,
    allocation: values,
  });
  resetForm({ values });
});
</script>

<i18n lang="json">
{
  "en": {
    "renter": "Renter",
    "bookingNumber": "Booking Number",
    "comment": "Comment",
    "driver": "Driver",
    "noDriverSelected": "No driver selected"
  },
  "de": {
    "renter": "Mieter",
    "bookingNumber": "Buchungsnummer",
    "comment": "Kommentar",
    "driver": "Fahrer",
    "noDriverSelected": "Kein Fahrer ausgewählt"
  },
  "es": {
    "renter": "Arrendatario",
    "bookingNumber": "N.º de reserva",
    "comment": "Comentario",
    "driver": "Conductor",
    "noDriverSelected": "Ningún conductor seleccionado"
  }
}
</i18n>
